import { useEffect, useState } from 'react';

export interface SectionHeroProps {
  eyebrow: string;
  headline: string;
  accentLine?: string;
  subheadline: string;
  image: string;
  imageAlt?: string;
  cta?: string;
}

export default function SectionHero({ eyebrow, headline, accentLine, subheadline, image, imageAlt, cta }: SectionHeroProps) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  const scrollToEstimate = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full min-h-[80vh] flex items-end overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={image} alt={imageAlt || headline} className="w-full h-full object-cover object-top" />
      </div>
      {/* Multi-layer overlays for depth */}
      <div className="absolute inset-0 bg-gradient-to-r from-ink/97 via-ink/85 to-ink/40"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-ink/30"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink/30"></div>

      {/* Gold accent elements */}
      <div className="absolute bottom-0 left-0 w-1/2 h-px bg-gradient-to-r from-gold/60 via-gold/20 to-transparent"></div>
      <div className="absolute top-0 left-0 w-1/3 h-px bg-gradient-to-r from-gold/30 to-transparent"></div>

      {/* Vertical structural line */}
      <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-b from-transparent via-gold/15 to-transparent hidden lg:block" style={{ left: '3rem' }}></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-48 pb-20">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div className={`flex items-center gap-3 mb-6 transition-all duration-800 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            style={{ transitionDuration: '800ms', transitionDelay: '100ms' }}>
            <span className="w-10 h-px bg-gold"></span>
            <span className="text-[10px] font-bold tracking-super uppercase text-gold/85">{eyebrow}</span>
          </div>

          {/* Headline */}
          <h1 className={`text-white leading-none mb-5 transition-all duration-800 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDuration: '800ms', transitionDelay: '200ms' }}>
            <span className="block font-condensed font-bold text-5xl md:text-7xl lg:text-8xl uppercase leading-none tracking-tighter">
              {headline}
            </span>
            {accentLine && (
              <span className="block font-serif italic text-2xl md:text-3xl text-gold mt-3 leading-snug">
                {accentLine}
              </span>
            )}
          </h1>

          {/* Divider */}
          <div className={`flex items-center gap-4 mb-6 transition-all duration-800 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            style={{ transitionDuration: '800ms', transitionDelay: '300ms' }}>
            <div className="w-16 h-px bg-gold/40"></div>
            <div className="w-2 h-2 rounded-full bg-gold/40"></div>
          </div>

          {/* Subheadline */}
          <p className={`text-white/55 text-base md:text-lg leading-relaxed max-w-xl mb-10 font-light transition-all duration-800 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            style={{ transitionDuration: '800ms', transitionDelay: '350ms' }}>
            {subheadline}
          </p>

          {/* CTAs */}
          <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-800 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            style={{ transitionDuration: '800ms', transitionDelay: '450ms' }}>
            <a href="#estimate" onClick={scrollToEstimate}
              className="inline-flex items-center justify-center gap-3 bg-gold text-ink font-bold text-[12px] tracking-widest uppercase px-10 py-4 rounded hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap group">
              <i className="ri-calendar-check-line text-sm"></i>
              {cta || 'Request a Free Estimate'}
            </a>
            <a href="tel:2285473887"
              className="inline-flex items-center justify-center gap-3 border border-white/20 text-white font-semibold text-[12px] tracking-widest uppercase px-10 py-4 rounded hover:border-gold/50 hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-gold text-sm"></i>
              (228) 547-3887
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
